import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const { items, isOpen, closeCart, updateQty, removeItem, subtotal, itemCount } = useCart();
  const navigate = useNavigate();

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeCart]);

  const handleCheckout = () => {
    if (!items.length) return;
    closeCart();
    // Wire-transfer-only checkout: collect customer + address details, create
    // a WC order with status="pending" / payment_method="wire_transfer" and
    // email branded wire instructions. No card collected here.
    navigate('/checkout');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/60 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[440px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-6 border-b border-neutral-200">
          <div>
            <div className="font-display tracking-[0.3em] text-[10px] text-[#FF6B00] font-semibold mb-1">
              YOUR CART
            </div>
            <div className="font-display text-2xl tracking-tight text-neutral-900 leading-none">
              {itemCount} ITEM{itemCount !== 1 ? 'S' : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={closeCart}
            data-testid="cart-drawer-close"
            aria-label="Close cart"
            className="h-10 w-10 flex items-center justify-center text-neutral-700 hover:text-[#FF6B00] transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-7 py-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-neutral-500">
              <ShoppingBag size={42} className="mb-4 text-neutral-300" />
              <p className="font-display tracking-[0.18em] text-sm mb-2">YOUR CART IS EMPTY</p>
              <p className="text-xs mb-6">Browse our catalog and add parts to start your build.</p>
              <Link
                to="/wheels"
                onClick={closeCart}
                className="inline-flex items-center gap-2 font-display tracking-[0.22em] text-xs font-bold text-[#FF6B00] hover:text-[#FF6B00]"
              >
                BROWSE WHEELS <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-200">
              {items.map((it) => (
                <li
                  key={it.lineId}
                  data-testid={`cart-line-${it.productId}`}
                  className="flex gap-4 py-5 first:pt-0 last:pb-0"
                >
                  <Link
                    to={`/product/${it.slug}`}
                    onClick={closeCart}
                    className="w-24 h-24 bg-neutral-50 border border-neutral-200 flex-shrink-0 overflow-hidden"
                  >
                    {it.image && (
                      <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <Link
                      to={`/product/${it.slug}`}
                      onClick={closeCart}
                      className="font-display text-[13px] tracking-wide text-neutral-900 hover:text-[#FF6B00] transition-colors block leading-snug pr-2"
                    >
                      {it.name}
                    </Link>
                    {it.variantOption && (
                      <div className="text-[10px] text-neutral-500 mt-1 font-display tracking-[0.18em]">
                        {it.variantOption.toUpperCase()}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 mt-auto pt-3">
                      <div className="flex items-center bg-white border border-neutral-400 rounded-sm overflow-hidden" data-testid={`cart-qty-${it.productId}`}>
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          onClick={() => updateQty(it.lineId, it.quantity - 1)}
                          className="h-9 w-9 flex items-center justify-center text-neutral-900 hover:bg-neutral-100 hover:text-[#FF6B00] active:bg-neutral-200 transition-colors border-r border-neutral-300"
                        >
                          <Minus size={14} strokeWidth={2.5} />
                        </button>
                        <span className="px-3 text-sm font-display tabular-nums min-w-[1.75rem] text-center text-neutral-900 select-none">{it.quantity}</span>
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          onClick={() => updateQty(it.lineId, it.quantity + 1)}
                          className="h-9 w-9 flex items-center justify-center text-neutral-900 hover:bg-neutral-100 hover:text-[#FF6B00] active:bg-neutral-200 transition-colors border-l border-neutral-300"
                        >
                          <Plus size={14} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-base tabular-nums text-neutral-900 leading-none">
                          ${(it.price * it.quantity).toLocaleString()}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(it.lineId)}
                          className="text-[10px] text-neutral-400 hover:text-[#FF6B00] mt-1.5 underline-offset-2 hover:underline transition-colors uppercase tracking-wider"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer / Checkout */}
        {items.length > 0 && (
          <div className="border-t border-neutral-200 px-7 py-6 space-y-4 bg-neutral-50">
            <div className="flex items-baseline justify-between">
              <span className="font-display tracking-[0.22em] text-[11px] text-neutral-500">
                SUBTOTAL
              </span>
              <span className="font-display text-2xl tabular-nums text-neutral-900 leading-none">
                ${subtotal.toLocaleString()}
                <span className="text-[11px] text-neutral-500 align-middle ml-1.5">USD</span>
              </span>
            </div>
            <p className="text-[11px] text-neutral-500 leading-relaxed" data-testid="cart-shipping-notice">
              No payment now &mdash; submit your request and we&rsquo;ll email a secure invoice to pay by card. Shipping is billed separately once your build is ready &mdash; please <Link to="/contact" className="underline">contact us</Link> for specific lead times.
            </p>
            <button
              type="button"
              onClick={handleCheckout}
              data-testid="cart-drawer-checkout"
              className="w-full bg-[#FF6B00] hover:bg-[#E25500] text-white py-4 font-display tracking-[0.22em] text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              CHECKOUT <ArrowRight size={16} />
            </button>
            <button
              type="button"
              onClick={closeCart}
              className="w-full border border-neutral-300 hover:border-neutral-900 text-neutral-700 hover:text-neutral-900 py-3 font-display tracking-[0.22em] text-xs font-bold transition-colors"
            >
              CONTINUE BROWSING
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
